import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  PUBLIC_OPEN_HOUSE_STATUSES, pickFeaturedOpenHouse, todayInTimezone, formatOpenHouseTime,
} from "@/lib/openHouseSchedule";

interface BannerEvent {
  title: string;
  event_date: string;
  start_time: string | null;
  end_time: string | null;
  end_time_approximate: boolean | null;
  location_name: string | null;
  city: string | null;
  status: string;
  public_visible: boolean;
}

function shortTime(start: string | null, end: string | null, approx: boolean) {
  const t = formatOpenHouseTime(start, end, approx);
  // "6:00 PM–7:30 PM" -> "6:00–7:30 PM" when both share the same suffix
  const m = t.match(/^(\d+:\d+) (AM|PM)–(\d+:\d+) (AM|PM)$/);
  return m && m[2] === m[4] ? `${m[1]}–${m[3]} ${m[4]}` : t;
}

const OpenHouseBanner = () => {
  const [event, setEvent] = useState<BannerEvent | null>(null);
  const ref = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    (async () => {
      const today = todayInTimezone();
      const { data } = await (supabase as any)
        .from("open_house_events")
        .select("title, event_date, start_time, end_time, end_time_approximate, location_name, city, status, public_visible")
        .eq("public_visible", true)
        .in("status", [...PUBLIC_OPEN_HOUSE_STATUSES])
        .gte("event_date", today)
        .order("event_date", { ascending: true })
        .limit(10);
      setEvent(pickFeaturedOpenHouse((data ?? []) as BannerEvent[], today));
    })();
  }, []);

  // Publish banner height so the fixed header sits directly below it.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = ref.current;
    if (!el) { root.style.setProperty("--announcement-h", "0px"); return; }
    const update = () => root.style.setProperty("--announcement-h", `${el.offsetHeight}px`);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { ro.disconnect(); root.style.setProperty("--announcement-h", "0px"); };
  }, [event]);

  if (!event) return null;

  const date = new Date(`${event.event_date}T12:00:00`).toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric",
  });
  const time = shortTime(event.start_time, event.end_time, !!event.end_time_approximate);
  const place = [event.location_name, event.city].filter(Boolean).join(", ");
  const parts = [date, time, place].filter(Boolean).join(" · ");

  return (
    <>
      <Link
        ref={ref}
        to="/open-house"
        className="public-site fixed top-0 left-0 right-0 z-[51] block bg-terracotta px-4 py-2.5 text-center text-sm font-bold text-primary-foreground hover:opacity-95 sm:text-base"
      >
        🎉 You're Invited! {event.title} — {parts} · Click for details &amp; to RSVP →
      </Link>
      <div aria-hidden style={{ height: "var(--announcement-h, 0px)" }} />
    </>
  );
};

export default OpenHouseBanner;
